<clp-toolbar [title]="('post.title' | translate)">
  <button
    type="button"
    class="clp-btn clp-btn-warning clp-btn-sm inline-flex items-center gap-2"
    (click)="navigateToCreate()">
    <i class="fa-solid fa-plus"></i>
    <span>{{ 'general.create' | translate }}</span>
  </button>
</clp-toolbar>

<div class="px-4 py-3">
  <div class="mb-3 rounded-lg border border-slate-700 bg-slate-900/55 p-2.5">
    <div class="grid gap-2 md:grid-cols-[240px_minmax(0,1fr)]">
      <div>
        <label class="mb-1 block text-xs uppercase tracking-wide text-slate-400">{{ 'post.community' | translate }}</label>
        <select class="clp-select" [value]="selectedCommunityId" (change)="onCommunityChange($event)">
          <option value="">{{ 'post.selectCommunity' | translate }}</option>
          @for (community of communities; track community.id ?? $index) {
            <option [value]="community.id">{{ community.name }}</option>
          }
        </select>
      </div>

      <div>
        <label class="mb-1 block text-xs uppercase tracking-wide text-slate-400">{{ 'post.content' | translate }}</label>
        <div class="relative">
          <i class="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500"></i>
          <input
            type="text"
            class="clp-input pl-9"
            [value]="searchTerm"
            (input)="onSearchChange($event)"
            [attr.placeholder]="'post.searchPlaceholder' | translate" />
        </div>
      </div>
    </div>
  </div>

  @if (!selectedCommunityId) {
    <div class="clp-card">
      <div class="clp-card-body flex flex-col items-center gap-3 py-10 text-center">
        <i class="fa-solid fa-users text-3xl text-slate-500"></i>
        <p class="text-sm text-slate-300">{{ 'post.noCommunitySelected' | translate }}</p>
      </div>
    </div>
  } @else if (loadingPosts) {
    <div class="clp-card">
      <div class="clp-card-body flex flex-col items-center gap-3 py-10 text-center">
        <i class="fa-solid fa-spinner fa-spin text-2xl text-slate-400"></i>
      </div>
    </div>
  } @else {
    @let filteredPosts = getFilteredPosts();

    @if (posts.length === 0) {
      <div class="clp-card">
        <div class="clp-card-body flex flex-col items-center gap-3 py-10 text-center">
          <i class="fa-solid fa-file-lines text-3xl text-slate-500"></i>
          <p class="text-sm text-slate-300">{{ 'post.noPosts' | translate }}</p>
        </div>
      </div>
    } @else if (filteredPosts.length === 0) {
      <div class="clp-card">
        <div class="clp-card-body flex flex-col items-center gap-2 py-8 text-center">
          <i class="fa-solid fa-filter-circle-xmark text-2xl text-slate-500"></i>
          <p class="text-sm text-slate-300">{{ 'post.noMatchingPosts' | translate }}</p>
        </div>
      </div>
    } @else {
      <div class="mx-auto max-w-5xl space-y-2">
        @for (post of filteredPosts; track post.id ?? $index) {
          <article class="group rounded-lg border border-slate-700 bg-slate-900/55 px-3 py-2.5 shadow-sm transition hover:border-amber-400/40 hover:bg-slate-900/75">
            <div class="min-w-0 space-y-2">
              <header class="flex items-start justify-between gap-2">
                <p class="text-xs text-slate-400">
                  {{ post.createdAt | date:'short' }}
                </p>

                <div class="flex shrink-0 items-center gap-1.5">
                  <button
                    class="clp-btn clp-btn-warning clp-btn-sm"
                    (click)="editPost(post)"
                    [attr.aria-label]="'general.edit' | translate"
                    [title]="'general.edit' | translate">
                    <i class="fa-regular fa-pen-to-square text-xs"></i>
                  </button>

                  <button
                    class="clp-btn clp-btn-danger clp-btn-sm"
                    (click)="openDeleteConfirmation(post)"
                    [attr.aria-label]="'general.delete' | translate"
                    [title]="'general.delete' | translate">
                    <i class="fa-solid fa-trash-can text-xs"></i>
                  </button>
                </div>
              </header>

                <div class="post-content-shell" [class.post-content-overflow]="isPostContentOverflow($index)">
                  <div
                    #postContentBlock
                    class="post-content text-sm text-slate-100"
                    [attr.data-post-index]="$index"
                    [innerHTML]="getRenderedContent(post.content)">
                  </div>
                </div>

                @if (getImageUrl(post.imageFile); as imageUrl) {
                  <img class="post-thumb mt-1" [src]="imageUrl" alt="" />
                }

                <div class="flex items-center gap-2 text-[11px] text-slate-400">
                  @if (post.publicPost) {
                    <span class="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-emerald-300">
                      {{ 'post.publicPost' | translate }}
                    </span>
                  }
                  @if ((post.videoType ?? 0) > 0 && post.videoLink) {
                    <span class="rounded-full border border-cyan-500/40 bg-cyan-500/10 px-2 py-0.5 text-cyan-300">
                      {{ 'post.videoType' | translate }}
                    </span>
                  }
                </div>
              </div>
          </article>
        }
      </div>
    }
  }
</div>

@if (postToDelete) {
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div
      class="absolute inset-0 bg-slate-950/70"
      (click)="closeDeleteConfirmation()"
      aria-hidden="true">
    </div>

    <section class="relative w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-2xl">
      <div class="mb-4 flex items-start gap-3">
        <span class="flex h-9 w-9 items-center justify-center rounded-full border border-red-500/40 bg-red-500/15 text-red-300">
          <i class="fa-solid fa-triangle-exclamation text-sm"></i>
        </span>
        <div class="min-w-0">
          <h3 class="text-base font-semibold text-slate-100">
            {{ 'post.delete.confirmTitle' | translate }}
          </h3>
          <p class="mt-1 text-sm text-slate-300">
            {{ 'post.delete.confirmMessage' | translate }}
          </p>
        </div>
      </div>

      <div class="flex justify-end gap-2">
        <button
          type="button"
          class="clp-btn clp-btn-secondary"
          [disabled]="deleting"
          (click)="closeDeleteConfirmation()">
          {{ 'general.cancel' | translate }}
        </button>
        <button
          type="button"
          class="clp-btn clp-btn-danger"
          [disabled]="deleting"
          (click)="confirmDeletePost()">
          {{ 'general.delete' | translate }}
        </button>
      </div>
    </section>
  </div>
}
